import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Check, Sparkles, Loader2, Crown, Leaf, Rocket } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_ICONS = {
  zephyr_essential_monthly: Leaf,
  zephyr_standard_monthly: Rocket,
  zephyr_premium_monthly: Crown,
};

const TIER_FEATURES = {
  zephyr_essential_monthly: {
    fr: ['Tous les tests (Fagerström, Q-MAT, Prochaska)', 'Dépistage pulmonaire', 'Suivi hebdomadaire', 'Bouton SOS envie'],
    en: ['All tests (Fagerström, Q-MAT, Prochaska)', 'Lung screening', 'Weekly tracking', 'SOS craving button'],
  },
  zephyr_standard_monthly: {
    fr: ['Tout Essentiel', 'Exercices TCC illimités', 'Chronologie des bénéfices santé', 'Badges et statistiques avancées', 'Support prioritaire'],
    en: ['Everything in Essential', 'Unlimited CBT exercises', 'Health benefits timeline', 'Badges & advanced stats', 'Priority support'],
  },
  zephyr_premium_monthly: {
    fr: ['Tout Standard', 'Coach IA personnalisé', 'Consultations vidéo (1/mois)', 'Programme personnalisé', 'Accès communauté privée'],
    en: ['Everything in Standard', 'AI personal coach', 'Video consultations (1/month)', 'Personalized program', 'Private community access'],
  },
};

export const Subscription = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/subscription/plans`)
      .then((r) => setPlans(r.data.plans))
      .catch(() => toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load plans'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async (lookup_key) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setCheckoutLoading(lookup_key);
    try {
      const res = await axios.post(
        `${API_URL}/payments/checkout`,
        { lookup_key, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(language === 'fr' ? 'Erreur de paiement' : 'Payment error');
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-emerald-50 via-white to-cyan-50 py-12 px-4" data-testid="subscription-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            {language === 'fr' ? '72h d\'essai gratuit sur tous les plans' : '72h free trial on all plans'}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">
            {language === 'fr' ? 'Investissez dans votre santé' : 'Invest in your health'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Choisissez le plan qui vous accompagnera vers une vie sans tabac. Aucun engagement, résiliez à tout moment.'
              : 'Choose the plan that will guide you to a smoke-free life. No commitment, cancel anytime.'}
          </p>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((p) => {
            const Icon = TIER_ICONS[p.lookup_key] || Leaf;
            const features = TIER_FEATURES[p.lookup_key]?.[language] || [];
            const isHighlight = p.highlight;
            return (
              <Card
                key={p.lookup_key}
                className={`relative rounded-3xl border-0 shadow-xl overflow-hidden animate-fade-in-up transition-transform hover:-translate-y-1 ${isHighlight ? 'ring-2 ring-emerald-500 md:scale-105' : ''}`}
                data-testid={`plan-${p.lookup_key}`}
              >
                {isHighlight && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-4 py-1 rounded-bl-2xl">
                    {language === 'fr' ? 'Recommandé' : 'Recommended'}
                  </div>
                )}
                <div className={`p-6 sm:p-8 ${isHighlight ? 'bg-gradient-to-br from-emerald-50 to-teal-50' : 'bg-white'}`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${isHighlight ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700'}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    {language === 'fr' ? p.name_fr : p.name_en}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-6 mt-4">
                    <span className="text-5xl font-black text-gray-900">
                      {p.amount_eur.toFixed(2).replace('.', ',')}
                    </span>
                    <span className="text-2xl text-gray-500">€</span>
                    <span className="text-sm text-gray-500 ml-1">
                      / {language === 'fr' ? 'mois' : 'month'}
                    </span>
                  </div>
                  <p className="text-xs text-emerald-700 font-semibold mb-4">
                    {language === 'fr' ? `Puis facturé après ${p.trial_period_days} jours d'essai gratuit` : `Billed after ${p.trial_period_days} days free trial`}
                  </p>
                  <ul className="space-y-2.5 mb-6">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleSubscribe(p.lookup_key)}
                    disabled={checkoutLoading === p.lookup_key}
                    className={`w-full h-12 rounded-full font-semibold shadow-lg ${isHighlight ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-900 hover:bg-gray-800'}`}
                    data-testid={`subscribe-btn-${p.lookup_key}`}
                  >
                    {checkoutLoading === p.lookup_key ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      language === 'fr' ? 'Commencer l\'essai gratuit' : 'Start free trial'
                    )}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Trust bar */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" />{language === 'fr' ? 'Paiement 100% sécurisé Stripe' : '100% secure Stripe payment'}</span>
          <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" />{language === 'fr' ? 'Résiliation en 1 clic' : '1-click cancellation'}</span>
          <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" />{language === 'fr' ? 'Sans engagement' : 'No commitment'}</span>
        </div>
      </div>
    </div>
  );
};
